import React from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import {
	FormatIndentDecrease,
	FormatIndentIncrease,
	FormatListBulleted,
	FormatListNumbered,
} from '@mui/icons-material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import ToolbarHeadingSelect from './ToolbarHeadingSelect';
import ToolbarColourPicker from './ToolbarColourPicker';
import ToolbarFontSelect from './ToolbarFontSelect';
import ToolbarFontSizeSelect from './ToolbarFontSizeSelect';
import ToolbarLineHeightSelect from './ToolbarLineHeightSelect';
import Signature from '@/components/TiptapEditor/Toolbar/Signature';
import RiskInfoDropdown from '@/components/TiptapEditor/Toolbar/Dropdowns/RiskInfoDropdown';
import DealDropdown from '@/components/TiptapEditor/Toolbar/Dropdowns/DealDropdown';
import ClientDropdown from '@/components/TiptapEditor/Toolbar/Dropdowns/ClientDropdown';
import BrokerDropdown from '@/components/TiptapEditor/Toolbar/Dropdowns/BrokerDropdown';
import UserDropdown from '@/components/TiptapEditor/Toolbar/Dropdowns/UserDropdown';
import ToolbarLinkButton from './ToolbarLinkButton';
import ToolbarImageButton from './ToolbarImageButton';
import ToolbarTableButton from './ToolbarTableButton';
import TableMenu from '../Menus/TableMenu';
import DivMenu from '../Menus/DivMenu';
import UserSignatureDropdown from './Dropdowns/UserSignatureDropdown';
import PresetTableDropdown from './Dropdowns/PresetTableDropdown';
import ToolbarContainerToggle from './ToolbarContainerToggle';

export const TooltipWrapper = ({ title, children }) => {
	return (
		<Tooltip title={title} placement='top' disableInteractive>
			<span>{children}</span>
		</Tooltip>
	);
};

const TiptapToolbar = () => {
	// const { user, brokerage, image, deal, client, riskInfo, align, table } = config;
	const { editor } = useCurrentEditor();

	const { isTableActive, isDivActive, ...config } = useEditorState({
		editor,
		selector: (ctx) => {
			const toolbarConfig = ctx.editor?.storage?.config?.toolbar;

			return {
				isTableActive: ['table', 'th', 'tr', 'td'].some((v) => ctx.editor?.isActive(v)),
				isDivActive: ['div'].some((v) => ctx.editor?.isActive(v)),
				user: toolbarConfig?.user,
				showUser: toolbarConfig?.user?.show,
				brokerage: toolbarConfig?.brokerage,
				showBrokerage: toolbarConfig?.brokerage?.show,
				image: toolbarConfig?.image,
				showImage: toolbarConfig?.image?.show,
				deal: toolbarConfig?.deal,
				showDeal: toolbarConfig?.deal?.show,
				presetTable: toolbarConfig?.presetTable,
				showPresetTable: toolbarConfig?.presetTable?.show,
				templateType: toolbarConfig?.templateType,
				client: toolbarConfig?.client,
				showClient: toolbarConfig?.client?.show,
				riskInfo: toolbarConfig?.riskInfo,
				showRiskInfo: toolbarConfig?.riskInfo?.show,
				align: toolbarConfig?.align,
				showAlign: toolbarConfig?.align?.show,
				table: toolbarConfig?.table,
				showTable: toolbarConfig?.table?.show,
				showContainerBorder: toolbarConfig?.containerBorder?.show,
			};
		},
	});

	if (!editor) return <></>;

	return (
		<Stack
			className='tiptap-toolbar'
			direction='column'
			pb={1}
			spacing={1}
			sx={{ padding: '6px' }}
		>
			<Stack
				direction='row'
				rowGap={1}
				columnGap={3}
				useFlexGap
				sx={(theme) => ({
					// color: 'rgba(0, 0, 0, 0.87)',

					alignItems: 'center',
					flexWrap: 'wrap',
					'& .MuiIconButton-root': {
						padding: '0px',
						transition: 'color 0.15s ease',

						'&:hover': {
							backgroundColor: 'transparent !important',
							color: theme.palette.primary.main,
						},

						'&.active, &.is-active': {
							color: theme.palette.primary.main,
						},

						'& .MuiTouchRipple-root': {
							display: 'none',
						},
					},
					'& .MuiSvgIcon-root': {
						fontSize: '18px',
					},
				})}
			>
				<Stack direction='row' spacing={1}>
					<TooltipWrapper title={'Font family'}>
						<ToolbarFontSelect />
					</TooltipWrapper>
					<TooltipWrapper title='Font size'>
						<ToolbarFontSizeSelect />
					</TooltipWrapper>
					<TooltipWrapper title='Heading'>
						<ToolbarHeadingSelect />
					</TooltipWrapper>
					<TooltipWrapper title='Line spacing'>
						<ToolbarLineHeightSelect />
					</TooltipWrapper>
				</Stack>

				<Stack direction='row' spacing={1}>
					<TooltipWrapper title='Text colour'>
						<ToolbarColourPicker />
					</TooltipWrapper>
					<TooltipWrapper title='Clear formatting'>
						<IconButton
							size='small'
							onClick={() =>
								editor.chain().focus().unsetAllMarks().clearNodes().run()
							}
						>
							<FormatClearIcon />
						</IconButton>
					</TooltipWrapper>
				</Stack>

				<Stack direction='row' spacing={1}>
					<TooltipWrapper title='Bold'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleBold().run()}
							className={editor?.isActive('bold') ? 'active' : ''}
						>
							<FormatBoldIcon />
						</IconButton>
					</TooltipWrapper>

					<TooltipWrapper title='Italics'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleItalic().run()}
							className={editor?.isActive('italic') ? 'active' : ''}
						>
							<FormatItalicIcon />
						</IconButton>
					</TooltipWrapper>
					<TooltipWrapper title='Underline'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleUnderline().run()}
							className={editor?.isActive('underline') ? 'active' : ''}
						>
							<FormatUnderlinedIcon />
						</IconButton>
					</TooltipWrapper>

					<TooltipWrapper title='Strikethrough'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleStrike().run()}
							className={editor?.isActive('strike') ? 'is-active' : ''}
						>
							<StrikethroughSIcon />
						</IconButton>
					</TooltipWrapper>
				</Stack>

				<Stack direction='row' spacing={1}>
					<TooltipWrapper title='Numbered list'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleOrderedList().run()}
							className={editor?.isActive('orderedList') ? 'active' : ''}
						>
							<FormatListNumbered />
						</IconButton>
					</TooltipWrapper>
					<TooltipWrapper title='Bullet list'>
						<IconButton
							size='small'
							onClick={() => editor.chain().focus().toggleBulletList().run()}
							className={editor?.isActive('bulletList') ? 'active' : ''}
						>
							<FormatListBulleted />
						</IconButton>
					</TooltipWrapper>

					<TooltipWrapper title='Increase indent'>
						<IconButton size='small' onClick={() => {}}>
							<FormatIndentDecrease />
						</IconButton>
					</TooltipWrapper>
					<TooltipWrapper title='Decrease indent'>
						<IconButton size='small' onClick={() => {}}>
							<FormatIndentIncrease />
						</IconButton>
					</TooltipWrapper>
				</Stack>

				{config.showAlign && (
					<Stack direction='row' spacing={1}>
						<TooltipWrapper title='Left align'>
							<IconButton
								size='small'
								onClick={() => editor.chain().focus().setTextAlign('left').run()}
								className={
									editor?.isActive({ textAlign: 'left' }) ? 'is-active' : ''
								}
							>
								<FormatAlignLeftIcon />
							</IconButton>
						</TooltipWrapper>
						<TooltipWrapper title='Centre align'>
							<IconButton
								size='small'
								onClick={() => editor.chain().focus().setTextAlign('center').run()}
								className={
									editor?.isActive({ textAlign: 'center' }) ? 'is-active' : ''
								}
							>
								<FormatAlignCenterIcon />
							</IconButton>
						</TooltipWrapper>

						<TooltipWrapper title='Right align'>
							<IconButton
								size='small'
								onClick={() => editor.chain().focus().setTextAlign('right').run()}
								className={
									editor?.isActive({ textAlign: 'right' }) ? 'is-active' : ''
								}
							>
								<FormatAlignRightIcon />
							</IconButton>
						</TooltipWrapper>
						<TooltipWrapper title='Justify'>
							<IconButton
								size='small'
								onClick={() => editor.chain().focus().setTextAlign('justify').run()}
								className={
									editor?.isActive({ textAlign: 'justify' }) ? 'is-active' : ''
								}
							>
								<FormatAlignJustifyIcon />
							</IconButton>
						</TooltipWrapper>
					</Stack>
				)}
				<Stack direction='row' spacing={1}>
					{config.showImage && (
						<TooltipWrapper title='Add image'>
							<ToolbarImageButton />
						</TooltipWrapper>
					)}
					<TooltipWrapper title='Link'>
						<ToolbarLinkButton />
					</TooltipWrapper>
					{config.showTable && (
						<TooltipWrapper title='Add table'>
							<ToolbarTableButton />
						</TooltipWrapper>
					)}
					{config.showContainerBorder && (
						<TooltipWrapper title='Toggle email container'>
							<ToolbarContainerToggle />
						</TooltipWrapper>
					)}
				</Stack>
			</Stack>
			<Stack direction='row' alignItems='center' sx={{ flexWrap: 'wrap' }}>
				{config.showUser && (
					<UserDropdown
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
						// onChange={(node) =>
						// 	editor.chain().focus().insertContent(node).unsetMark('customSpan').run()
						// }
						showSignature={config.user?.showSignature}
					/>
				)}
				{config.showBrokerage && (
					<BrokerDropdown
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
				)}
				{config.showClient && (
					<ClientDropdown
						clientId={config.client?.clientId}
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
				)}
				{config.showDeal && (
					<DealDropdown
						dealId={config.deal?.dealId}
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
				)}
				{config.showPresetTable && (
					<PresetTableDropdown
						dealId={config.deal?.dealId}
						clientId={config.client?.clientId}
						templateType={config.templateType}
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
				)}
				{config.showRiskInfo && (
					<RiskInfoDropdown
						dealId={config.deal?.dealId}
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
				)}
				{config.user?.showSignature && (
					<UserSignatureDropdown
						onChange={(node) => editor.chain().focus().insertContent(node).run()}
					/>
					// <Signature
					// 	onChange={(node) => editor.chain().focus().insertContent(node).run()}
					// 	// disabled={disableSignature}
					// />
				)}
			</Stack>
			{isTableActive && (
				<Box
					width='100%'
					px='8px'
					pt={'0.25em'}
					sx={{ borderTop: '1px solid rgb(230, 233, 242)' }}
				>
					<TableMenu />
				</Box>
			)}
			{isDivActive && (
				<Box
					width='100%'
					px='8px'
					pt={'0.25em'}
					sx={{ borderTop: '1px solid rgb(230, 233, 242)' }}
				>
					<DivMenu />
				</Box>
			)}
		</Stack>
	);
};

export default TiptapToolbar;
